import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    ReactNode,
    forwardRef,
} from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import cx from 'classnames';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import _capitalize from 'lodash/capitalize';
import { useDeepCompareCallback } from 'use-deep-compare';
import { Transition } from '@headlessui/react';

import {
    addThousandsSeparator,
    dateRangesAreEqual,
    filterInputToDateRange,
    formatDetectionCountry,
    formatTableString,
    getScaledColumnWidths,
    getScrollbarWidth,
    getURLSearchParams,
    locale,
    mapPreFilterValuesToExportDetectionsFilter,
} from '@trqk/utils';
import {
    DateTimeCell,
    IconInfoCircle,
    IconDownloadedFile,
    LoadingSpinner,
    MatchReferenceCell,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TimeCodeCell,
    TitleFilenameCell,
    Tooltip,
    TruncatedListCell,
    TruncatedTwoRowCell,
    IconFilter,
} from '@trqk/components';

import { DetectionTotalsChart } from './DetectionTotalsChart';
import {
    columnIds,
    columnWidthInputs,
    detectionCountryMap,
    defaultDateRangeOption,
    preFilterConfigs,
} from '../constants';
import { getWorkDetailPath } from '../../../../utils';
import { useExportDetections } from '../../../../hooks';
import { DownloadAs } from '../../../tooltips';
import { URL_PARAMS } from '../../../../constants';

const GET_DETECTIONS_UI_STATE = gql`
    query getDetectionsUiState {
        uiState @client {
            detections {
                showChart
            }
        }
    }
`;

const rowHeight = 50;
const chartHeight = 250;
const defaultColumnWidth = 100;

interface DetectionsTableProps {
    /**
     * Array of {@link DetectionsTableItem} objects
     */
    detections: DetectionsTableItem[];
    /**
     * Array of DetectionTotal objects or null, to render in DetectionTotalsChart
     */
    detectionTotals: DetectionTotal[] | null;
    /**
     * Query filters to set the current filter input value
     */
    filters: ReactNode;
    /**
     * Current filter input value
     */
    filterInput: Record<
        string,
        Maybe<TableStringFilterInput | TableIntFilterInput>
    >;
    /**
     * getSize method returned by useAutoSize
     */
    getSize(): void;
    /**
     * Table height
     */
    height: number;
    /**
     * Whether data is currently being fetched && mapped into DetectionsTableItems
     */
    loading: boolean;
    /**
     * The `preFilterState` exposed by useDetectionsTableItems()
     */
    preFilterState: PreFilterState<DetectionsTableItem>;
    /**
     * A timestamp for refreshing the table. When this changes, it indicates the table should be
     * updated with new data
     */
    refreshTime: number;
    /**
     * Table width
     */
    width: number;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * A table of statement performance data, featuring filters and column sorting.
 * @param {DetectionsTableProps} props
 * @returns JSX.Element
 */
export const DetectionsTable = forwardRef<HTMLDivElement, DetectionsTableProps>(
    (
        {
            detections,
            detectionTotals,
            filters,
            filterInput,
            getSize,
            height,
            loading,
            preFilterState,
            refreshTime,
            width,
        }: DetectionsTableProps,
        ref,
    ): JSX.Element => {
        const [data, setData] = useState(detections);
        const detectionsUiStateQuery = useQuery(GET_DETECTIONS_UI_STATE);
        const showChart =
            detectionsUiStateQuery?.data?.uiState?.detections?.showChart ??
            true;
        const client = useApolloClient();
        const { exportDetections, exportingDetections } = useExportDetections();

        const filterInputDateRange = filterInputToDateRange(
            filterInput?.timestamp ?? null,
        );

        const isExportingDetections = exportingDetections.some(item =>
            dateRangesAreEqual(item.dateRange, filterInputDateRange),
        );

        const setShowChart = (nextShowChart: boolean): void => {
            client.writeQuery({
                query: GET_DETECTIONS_UI_STATE,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        detections: {
                            __typename: 'Detections',
                            showChart: nextShowChart,
                        },
                    },
                },
            });
        };

        // Only reset the items data state when refreshTime is greater than zero
        useEffect(() => {
            if (refreshTime > 0) {
                setData(detections);
            }
        }, [detections, refreshTime]);

        // Resize the table after the chart is hidden or shown
        useEffect(() => {
            const timeoutId = window.setTimeout(() => getSize, 500);

            return (): void => {
                window.clearTimeout(timeoutId);
            };
        }, [showChart, getSize]);

        /* BEGIN table management logic */

        const defaultColumn = useMemo(
            () => ({
                width: defaultColumnWidth,
            }),
            [],
        );

        const {
            location: { search },
        } = useHistory();

        const columns = useMemo(() => {
            const scaledColumnWidths = getScaledColumnWidths(
                width,
                columnWidthInputs,
            );

            return [
                {
                    id: columnIds.timestamp,
                    accessor: ({ timestamp }: DetectionsTableItem): string =>
                        timestamp,
                    headerLabel: 'Date & Time (UTC)',
                    Header: TableHeader,
                    Cell: DateTimeCell,
                    width:
                        scaledColumnWidths?.[columnIds.timestamp] ??
                        defaultColumnWidth,
                    sortType: (
                        rowA: Row<DetectionsTableItem>,
                        rowB: Row<DetectionsTableItem>,
                    ): number => {
                        const detectionStartA = new Date(
                            rowA?.original?.timestamp ?? 0,
                        );
                        const detectionStartB = new Date(
                            rowB?.original?.timestamp ?? 0,
                        );

                        if (detectionStartA < detectionStartB) {
                            return 1;
                        }

                        if (detectionStartA > detectionStartB) {
                            return -1;
                        }

                        return 0;
                    },
                },
                {
                    id: columnIds.matchReference,
                    accessor: ({
                        match_reference,
                    }: DetectionsTableItem): Maybe<string> =>
                        match_reference ?? null,
                    headerLabel: 'Reference',
                    Header: TableHeader,
                    Cell: MatchReferenceCell,
                    width:
                        scaledColumnWidths?.[columnIds.matchReference] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.duration,
                    accessor: ({
                        duration,
                    }: DetectionsTableItem): Maybe<number> => duration ?? null,
                    headerLabel: 'Duration',
                    Header: TableHeader,
                    Cell: TimeCodeCell,
                    width:
                        scaledColumnWidths?.[columnIds.duration] ??
                        defaultColumnWidth,
                },
                // confidence
                {
                    id: columnIds.confidence,
                    accessor: ({ confidence }: DetectionsTableItem): string =>
                        _capitalize(formatTableString(confidence)),
                    Header: TableHeader,
                    headerLabel: 'Confidence',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.confidence] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.titleFilename,
                    accessor: ({
                        audio_deleted,
                        audio_id,
                        filename,
                        song_id,
                        title,
                    }: DetectionsTableItem): {
                        audioDeleted: boolean;
                        filename: Maybe<string>;
                        title: Maybe<string>;
                        trqkAudioId: Maybe<string>;
                        trqkSongId: Maybe<string>;
                        href: Maybe<string>;
                    } => {
                        const audioDeleted = audio_deleted ?? false;

                        const href = getWorkDetailPath({
                            audioDeleted,
                            search,
                            trqkAudioId: audio_id ?? null,
                            trqkSongId: song_id ?? null,
                        });

                        return {
                            audioDeleted,
                            filename: filename ?? null,
                            href,
                            title,
                            trqkAudioId: audio_id ?? null,
                            trqkSongId: song_id ?? null,
                        };
                    },
                    Header: TableHeader,
                    headerLabel: 'Work Title / Audio',
                    Cell: TitleFilenameCell,
                    width:
                        scaledColumnWidths?.[columnIds.titleFilename] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.station,
                    accessor: ({ channel }: DetectionsTableItem): string =>
                        formatTableString(channel),
                    Header: TableHeader,
                    headerLabel: 'Station',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.station] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.country,
                    accessor: ({ country }: DetectionsTableItem): string =>
                        formatDetectionCountry(detectionCountryMap, country),
                    Header: TableHeader,
                    headerLabel: 'Country',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.country] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.show,
                    accessor: ({ show }: DetectionsTableItem): string =>
                        formatTableString(show),
                    Header: TableHeader,
                    headerLabel: 'Show',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.show] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.productionCompany,
                    accessor: ({
                        program_production_company,
                    }: DetectionsTableItem): string =>
                        formatTableString(program_production_company),
                    Header: TableHeader,
                    headerLabel: 'Production Company',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.productionCompany] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.proId,
                    accessor: ({
                        workIds,
                    }: DetectionsTableItem): Maybe<Maybe<string>[]> => workIds,
                    Header: TableHeader,
                    headerLabel: 'PRO ID',
                    Cell: TruncatedListCell,
                    width:
                        scaledColumnWidths?.[columnIds.proId] ??
                        defaultColumnWidth,
                },
                {
                    id: columnIds.publisher,
                    accessor: ({ publisher }: DetectionsTableItem): string =>
                        formatTableString(publisher),
                    Header: TableHeader,
                    headerLabel: 'Publisher',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.publisher] ??
                        defaultColumnWidth,
                    tooltipPosition: 'left',
                },
                {
                    id: columnIds.writer,
                    accessor: ({ writer }: DetectionsTableItem): string =>
                        formatTableString(writer),
                    Header: TableHeader,
                    headerLabel: 'Writer',
                    Cell: TruncatedTwoRowCell,
                    width:
                        scaledColumnWidths?.[columnIds.writer] ??
                        defaultColumnWidth,
                    tooltipPosition: 'left',
                },
            ];
        }, [search, width, detectionCountryMap]);

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            totalColumnsWidth,
            prepareRow,
        } = useTable<DetectionsTableItem>(
            {
                columns,
                data,
                defaultColumn,
                initialState: {
                    sortBy: [
                        {
                            id: 'timestamp',
                            desc: false,
                        },
                    ],
                },
            },
            useBlockLayout,
            useSortBy,
        );

        /* END table management logic */

        const RenderRow = useCallback(
            ({ index, style }) => {
                const row = rows[index];

                prepareRow(row);

                return (
                    <TableRow<DetectionsTableItem>
                        index={index}
                        style={style}
                        row={row}
                    />
                );
            },
            [prepareRow, rows],
        );

        const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

        const dateRangeLabel =
            getURLSearchParams({
                search,
                configs: [
                    {
                        defaultValue: defaultDateRangeOption.label,
                        paramKey: URL_PARAMS.DATE_LABEL,
                    },
                ],
            })?.[URL_PARAMS.DATE_LABEL] ?? null;

        const disableExportDetections =
            isExportingDetections || rows.length < 1;

        const preFilterValues = Array.from(
            preFilterState.context.preFilterValues.values(),
        );

        const handleDownloadAs = useDeepCompareCallback(
            (downloadAs: string) => {
                const filter = mapPreFilterValuesToExportDetectionsFilter(
                    preFilterValues,
                    preFilterConfigs,
                );

                if (filterInput.timestamp) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    filter.timestamp = filterInput.timestamp;
                }

                exportDetections(filter, downloadAs);
            },
            [exportDetections, filterInput, preFilterValues, preFilterConfigs],
        );

        return (
            <div
                {...getTableProps()}
                className="mx-6 text-sm font-normal trqk-table"
            >
                {filters}
                <div className="flex items-baseline">
                    <div className="mt-2 ml-1 mr-2 text-lg font-semibold">
                        {locale.enUS.DETECTIONS.DETECTION_TOTALS}
                    </div>
                    <button
                        type="button"
                        className="text-sm text-primary-500"
                        onClick={(): void => {
                            setShowChart(!showChart);
                        }}
                    >
                        {showChart &&
                            `[${locale.enUS.DETECTIONS.BUTTONS.HIDE}]`}
                        {!showChart &&
                            `[${locale.enUS.DETECTIONS.BUTTONS.SHOW}]`}
                    </button>
                </div>
                <Transition
                    show={showChart}
                    enter="transition-opacity duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterEnter={getSize}
                    afterLeave={getSize}
                >
                    <DetectionTotalsChart
                        height={chartHeight}
                        setTableDimensions={getSize}
                        detectionTotals={detectionTotals}
                    />
                </Transition>
                <div className="flex items-center justify-end gap-x-6">
                    <div className="flex items-center">
                        <IconFilter
                            fillColor={
                                rows.length > 0 ? 'primary-500' : 'tertiary-300'
                            }
                            // height={26}
                        />
                        <p>
                            {locale.enUS.DETECTIONS.ROW_COUNT}:{' '}
                            <span
                                className="inline-block"
                                style={{ minWidth: 65 }}
                            >
                                {addThousandsSeparator(rows.length)}
                            </span>
                        </p>
                    </div>
                    <div style={{ marginTop: 4 }}>
                        <Tooltip<DownloadAsTooltipMessageProps>
                            addClasses={cx({
                                'cursor-default text-tertiary-300':
                                    disableExportDetections,
                            })}
                            disabled={disableExportDetections}
                            trigger="click"
                            RenderMessage={DownloadAs}
                            renderMessageProps={{
                                body: (
                                    <>
                                        {dateRangeLabel && (
                                            <p>
                                                {
                                                    locale.enUS.DETECTIONS
                                                        .DETECTIONS_FROM
                                                }{' '}
                                                <span className="font-semibold">
                                                    {dateRangeLabel}
                                                </span>
                                            </p>
                                        )}
                                    </>
                                ),
                                fileExtension: '.csv',
                                handleDownloadAs,
                            }}
                            minWidth={450}
                            maxWidth={450}
                            position="left"
                            background="light"
                        >
                            <p className="flex items-center">
                                {!isExportingDetections && (
                                    <IconDownloadedFile
                                        width={20}
                                        fillColor={
                                            disableExportDetections
                                                ? 'tertiary-300'
                                                : 'primary-500'
                                        }
                                    />
                                )}
                                {isExportingDetections && (
                                    <LoadingSpinner width={20} />
                                )}
                                <span
                                    className={cx(
                                        'ml-2 text-sm font-medium underline',
                                        {
                                            'text-primary-500':
                                                !disableExportDetections,
                                            'text-tertiary-400':
                                                disableExportDetections,
                                        },
                                    )}
                                >
                                    {locale.enUS.DETECTIONS.BUTTONS.DOWNLOAD_AS}
                                </span>
                            </p>
                        </Tooltip>
                    </div>
                </div>
                <div className="mt-4">
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<DetectionsTableItem>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<DetectionsTableItem>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div ref={ref} {...getTableBodyProps()}>
                    {loading && (
                        <div
                            style={{
                                height,
                                width: totalColumnsWidth,
                            }}
                            className="flex items-center justify-center"
                        >
                            <LoadingSpinner />
                        </div>
                    )}
                    {!loading && (
                        <>
                            {rows.length === 0 && (
                                <div
                                    className="flex items-center justify-center italic text-tertiary-400"
                                    style={{ height: 300 }}
                                >
                                    <div className="flex items-center">
                                        <IconInfoCircle
                                            addClasses="mr-2"
                                            fillColor="primary-500"
                                            width={20}
                                        />
                                        {
                                            locale.enUS.DETECTIONS
                                                .CURRENT_DATA_EMPTY
                                        }
                                    </div>
                                </div>
                            )}
                            <FixedSizeList
                                height={height}
                                itemCount={rows.length}
                                itemSize={rowHeight}
                                width={totalColumnsWidth + scrollbarWidth}
                            >
                                {RenderRow}
                            </FixedSizeList>
                        </>
                    )}
                </div>
            </div>
        );
    },
);
